import type { IconConfig } from '../types'
import { loadTwemojiAsset, type TwemojiAsset } from '../utils/twemoji'
import { ensureCanvasReadable } from './canvasProtection'
import { CanvasRenderer, type IconRenderer, SvgRenderer } from './render'

export interface RenderOptions {
  twemojis?: ReadonlyMap<string, TwemojiAsset>
}

export class IconBuilder {
  constructor(private readonly config: IconConfig) {}

  renderTo(renderer: IconRenderer, options: RenderOptions = {}): void {
    const { corners, background, innerFrame } = this.config

    renderer.drawOuterFrame(corners, background)

    if (innerFrame.enabled) {
      renderer.drawInnerFrame(corners, innerFrame.background, innerFrame.inset)
    }

    for (const item of this.config.contents) {
      if (item.type === 'image') {
        renderer.drawImage(item.image, item)
      } else if (item.type === 'emoji') {
        renderer.drawEmoji(item, options.twemojis?.get(item.emoji) ?? null)
      }
    }
  }

  async resolveOptions(): Promise<RenderOptions> {
    const twemojis = new Map<string, TwemojiAsset>()
    const keys = this.config.useTwemoji ? this.collectEmojis() : []

    await Promise.all(
      keys.map(async (emoji) => {
        const asset = await loadTwemojiAsset(emoji)

        if (asset) {
          twemojis.set(emoji, asset)
        }
      }),
    )

    return { twemojis }
  }

  private collectEmojis(): string[] {
    const out: string[] = []

    for (const item of this.config.contents) {
      if (item.type === 'emoji') {
        out.push(item.emoji)
      }
    }

    return out
  }

  async toPNG(size: number): Promise<Blob> {
    ensureCanvasReadable()

    const options = await this.resolveOptions()
    const canvas = document.createElement('canvas')

    canvas.width = size
    canvas.height = size

    const context = canvas.getContext('2d')

    if (!context) {
      throw new Error('Canvas 2D context unavailable')
    }

    const renderer = new CanvasRenderer(context, size)

    this.renderTo(renderer, options)
    renderer.finalize()

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('toBlob returned null'))
        }
      }, 'image/png')
    })
  }

  async toSVG(): Promise<string> {
    const options = await this.resolveOptions()
    const renderer = new SvgRenderer()

    this.renderTo(renderer, options)

    return renderer.toString()
  }
}
